import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./kassa.css";
import "../finance/finance.css";
import { fmt } from "../finance/finance";

function BalanceCard({ label, sub, currencyCode, balance, editMode, onDelete, onHistory, visibility }) {
    const [historyOpen, setHistoryOpen] = useState(false);
    const [history, setHistory] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const toggleHistory = async () => {
        if (historyOpen) { setHistoryOpen(false); return; }
        setHistoryOpen(true);
        if (!history) {
            try {
                const data = await onHistory();
                setHistory(data);
            } catch (err) {
                console.error("history:", err);
                toast.error("Не удалось загрузить историю");
            }
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Удалить «${label}»? История сохранится.`)) return;
        setDeleting(true);
        try {
            await onDelete();
        } finally {
            setDeleting(false);
        }
    };

    const symbol = currencyCode === "USD" ? "$" : "";
    const suffix = currencyCode === "USD" ? "" : ` ${currencyCode}`;

    return (
        <div className="kz-card">
            {editMode && (
                <button className="kz-delete-btn" onClick={handleDelete} disabled={deleting} title="Удалить">✕</button>
            )}
            <div className="kz-name">{label}</div>
            {sub && <div className="kz-sub">{sub}</div>}
            <div className="kz-val">{symbol}{fmt(balance)}{suffix}</div>

            {editMode && visibility && (
                <div className="kz-visibility">
                    <label className="kz-visibility-item">
                        <input
                            type="checkbox"
                            checked={visibility.showInIncome}
                            onChange={(e) => visibility.onToggle("show_in_income", e.target.checked)}
                        />
                        Приход
                    </label>
                    <label className="kz-visibility-item">
                        <input
                            type="checkbox"
                            checked={visibility.showInExpense}
                            onChange={(e) => visibility.onToggle("show_in_expense", e.target.checked)}
                        />
                        Расход
                    </label>
                </div>
            )}

            <button className="kz-history-btn" onClick={toggleHistory}>{historyOpen ? "Скрыть" : "История"}</button>

            {historyOpen && (
                <div className="kz-history">
                    {history == null ? (
                        <div className="kz-history-empty">Загрузка…</div>
                    ) : history.length === 0 ? (
                        <div className="kz-history-empty">Пока пусто</div>
                    ) : (
                        history.map((h, i) => (
                            <div className="kz-history-row" key={i}>
                                <span className="kz-h-label">{h.label} · {new Date(h.date).toLocaleDateString("ru-RU")}</span>
                                <span className="kz-h-amt" style={{ color: h.kind === "expense" ? "var(--fn-red)" : "var(--fn-green)" }}>
                                    {h.kind === "expense" ? "−" : "+"}{symbol}{fmt(h.amount)}{suffix}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
//  Корректировка баланса — ручная правка, не приход/расход.
//  Видит ВСЕ кассы/карты вне зависимости от птичек приход/расход —
//  это админский инструмент, а не пользовательская форма.
//  История видна в Операциях как отдельный вид записи.
// ─────────────────────────────────────────────────────────────
function AdjustBalanceForm({ API_URL, paymentTypes = [], kassaList = [], onSaved, onClose }) {
    const [target, setTarget] = useState("");
    const [sign, setSign] = useState("plus"); // 'plus' | 'minus'
    const [amount, setAmount] = useState("");
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!target) { toast.error("Выберите кассу"); return; }
        if (!amount || Number(amount) <= 0) { toast.error("Укажите сумму"); return; }
        if (!comment.trim()) { toast.error("Укажите причину корректировки"); return; }
        const [kind, id] = target.split(":");
        const signedAmount = sign === "minus" ? -Math.abs(Number(amount)) : Math.abs(Number(amount));
        setSaving(true);
        try {
            await axios.post(`${API_URL}/addBalanceAdjustment`, {
                paymentTypeId: kind === "pt" ? Number(id) : null,
                kassaId: kind === "k" ? Number(id) : null,
                amount: signedAmount,
                comment,
            });
            toast.success("Баланс скорректирован");
            onSaved();
            onClose();
        } catch (err) {
            console.error("addBalanceAdjustment:", err);
            toast.error("Не удалось сохранить");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="kz-addform">
            <div className="fn-hint" style={{ marginTop: 0 }}>
                Для исправления расхождений баланса (недостача/излишек), не связанных с обычным приходом или расходом.
                История видна на странице «Операции» с пометкой «Корректировка».
            </div>
            <div className="fn-fgrid two">
                <div className="fn-field">
                    <label>Касса</label>
                    <select value={target} onChange={(e) => setTarget(e.target.value)}>
                        <option value="">— выбрать —</option>
                        {paymentTypes.filter((pt) => !pt.isCard).map((pt) => <option key={`pt-${pt.id}`} value={`pt:${pt.id}`}>{pt.name}</option>)}
                        {kassaList.map((k) => <option key={`k-${k.id}`} value={`k:${k.id}`}>{paymentTypes.find((pt) => pt.id === k.paymentTypeId)?.name} — {k.recipient}</option>)}
                    </select>
                </div>
                <div className="fn-field">
                    <label>Сумма</label>
                    <div style={{ display: "flex", gap: 8 }}>
                        <select value={sign} onChange={(e) => setSign(e.target.value)} style={{ width: 64 }}>
                            <option value="plus">+</option>
                            <option value="minus">−</option>
                        </select>
                        <input type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
                    </div>
                </div>
            </div>
            <div className="fn-field">
                <label>Причина корректировки</label>
                <input placeholder="Например: пересчёт кассы, найдена недостача" value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
            <button className="fn-btn-add-payment" onClick={submit} disabled={saving} style={{ background: "var(--fn-navy)" }}>
                {saving ? "Сохраняем…" : "Скорректировать баланс"}
            </button>
        </div>
    );
}

const LIST_SOURCE_LABELS = {
    guide: "Гиды",
    cafe: "Кафе",
    driver4x4: "Водители 4x4",
    driverbus: "Водители автобусов",
    driversedan: "Водители седанов",
    driver4x4other: "Водители 4x4 (прочие)",
    cars: "Автомобили 4х4",
};

const listSourceLabels = (listSource) =>
    (listSource || "").split(",").filter(Boolean).map((k) => LIST_SOURCE_LABELS[k] || k);

const Kassa = ({ API_URL }) => {
    const navigate = useNavigate();
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [kassaList, setKassaList] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editMode, setEditMode] = useState(false);
    const clickCountRef = useRef(0);
    const clickTimerRef = useRef(null);

    const handleTitleClick = () => {
        clickCountRef.current += 1;
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        clickTimerRef.current = setTimeout(() => { clickCountRef.current = 0; }, 1500);
        if (clickCountRef.current >= 5) {
            clickCountRef.current = 0;
            setEditMode((v) => {
                toast.success(!v ? "Режим редактирования включён" : "Режим редактирования выключен");
                return !v;
            });
        }
    };

    const [ptOpen, setPtOpen] = useState(false);
    const [ptName, setPtName] = useState("");
    const [ptIsCard, setPtIsCard] = useState(false);
    const [ptCurrencyId, setPtCurrencyId] = useState(null);
    const [savingPt, setSavingPt] = useState(false);

    const [addCardOpen, setAddCardOpen] = useState(false);
    const [cardTypeId, setCardTypeId] = useState(null);
    const [cardRecipient, setCardRecipient] = useState("");
    const [cardCurrencyId, setCardCurrencyId] = useState(null);
    const [savingCard, setSavingCard] = useState(false);

    const [curOpen, setCurOpen] = useState(false);
    const [curCode, setCurCode] = useState("");
    const [curName, setCurName] = useState("");
    const [savingCur, setSavingCur] = useState(false);

    const [catOpen, setCatOpen] = useState(false);
    const [catName, setCatName] = useState("");
    const [catParentId, setCatParentId] = useState("");
    const [catListSources, setCatListSources] = useState([]); // теперь массив — можно выбрать несколько
    const [savingCat, setSavingCat] = useState(false);

    const [adjustOpen, setAdjustOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ptRes, kassaRes, curRes, catRes] = await Promise.all([
                axios.post(`${API_URL}/getPaymentTypes`),
                axios.post(`${API_URL}/getKassaList`),
                axios.post(`${API_URL}/getCurrencies`),
                axios.post(`${API_URL}/getExpenseCategories`),
            ]);
            if (ptRes.data.status === "success") {
                setPaymentTypes(ptRes.data.paymentTypes);
                setPtCurrencyId((prev) => prev ?? ptRes.data.paymentTypes.find((p) => p.currencyId)?.currencyId ?? null);
            }
            if (kassaRes.data.status === "success") setKassaList(kassaRes.data.kassaList);
            if (curRes.data.status === "success") {
                setCurrencies(curRes.data.currencies);
                setPtCurrencyId((prev) => prev ?? curRes.data.currencies[0]?.id ?? null);
                setCardCurrencyId((prev) => prev ?? curRes.data.currencies[0]?.id ?? null);
            }
            if (catRes.data.status === "success") setCategories(catRes.data.categories);
        } catch (err) {
            console.error("load kassa page:", err);
        } finally {
            setLoading(false);
        }
    }, [API_URL]);

    useEffect(() => { load(); }, [load]);

    const regularTypes = paymentTypes.filter((pt) => !pt.isCard);
    const cardTypes = paymentTypes.filter((pt) => pt.isCard);
    const mainCats = categories.filter((c) => !c.parentId);
    const subsOf = (id) => categories.filter((c) => c.parentId === id);

    // птичка "Приход/Расход" у обычного типа оплаты (не карты)
    const togglePtVisibility = async (pt, field, value) => {
        setPaymentTypes((prev) => prev.map((p) => (p.id === pt.id ? { ...p, [field === "show_in_income" ? "showInIncome" : "showInExpense"]: value } : p)));
        try {
            await axios.post(`${API_URL}/updatePaymentTypeVisibility`, { id: pt.id, field, value });
        } catch (err) {
            console.error("updatePaymentTypeVisibility:", err);
            toast.error("Не удалось сохранить — обновите страницу");
            await load();
        }
    };

    // птичка "Приход/Расход" у конкретной карты — у каждой своя, независимо от остальных карт того же типа
    const toggleKassaVisibility = async (k, field, value) => {
        setKassaList((prev) => prev.map((row) => (row.id === k.id ? { ...row, [field === "show_in_income" ? "showInIncome" : "showInExpense"]: value } : row)));
        try {
            await axios.post(`${API_URL}/updateKassaVisibility`, { id: k.id, field, value });
        } catch (err) {
            console.error("updateKassaVisibility:", err);
            toast.error("Не удалось сохранить — обновите страницу");
            await load();
        }
    };

    const submitPaymentType = async () => {
        if (!ptName.trim()) { toast.error("Укажите название"); return; }
        if (!ptIsCard && !ptCurrencyId) { toast.error("Выберите валюту"); return; }
        setSavingPt(true);
        try {
            await axios.post(`${API_URL}/addPaymentType`, { name: ptName, isCard: ptIsCard, currencyId: ptIsCard ? null : ptCurrencyId });
            toast.success("Добавлено");
            setPtName(""); setPtIsCard(false); setPtOpen(false);
            await load();
        } catch (err) {
            console.error("addPaymentType:", err);
            toast.error("Не удалось добавить");
        } finally {
            setSavingPt(false);
        }
    };

    const deletePaymentType = async (pt) => {
        const cardsOfType = kassaList.filter((k) => k.paymentTypeId === pt.id);
        const warn = cardsOfType.length ? ` Все ${cardsOfType.length} карты этого типа тоже пропадут из списка.` : "";
        if (!window.confirm(`Удалить «${pt.name}»?${warn}`)) return;
        try {
            await axios.post(`${API_URL}/deletePaymentType`, { id: pt.id });
            toast.success("Удалено");
            await load();
        } catch (err) {
            console.error("deletePaymentType:", err);
            toast.error("Не удалось удалить");
        }
    };

    const submitCard = async () => {
        if (!cardTypeId) { toast.error("Выберите тип оплаты"); return; }
        if (!cardRecipient.trim()) { toast.error("Укажите получателя"); return; }
        if (!cardCurrencyId) { toast.error("Выберите валюту"); return; }
        setSavingCard(true);
        try {
            await axios.post(`${API_URL}/addKassa`, { paymentTypeId: cardTypeId, currencyId: cardCurrencyId, recipient: cardRecipient });
            toast.success("Карта добавлена");
            setCardRecipient(""); setAddCardOpen(false);
            await load();
        } catch (err) {
            console.error("addKassa:", err);
            toast.error("Не удалось добавить карту");
        } finally {
            setSavingCard(false);
        }
    };

    const submitCurrency = async () => {
        if (!curCode.trim() || !curName.trim()) { toast.error("Укажите код и название"); return; }
        setSavingCur(true);
        try {
            await axios.post(`${API_URL}/addCurrency`, { code: curCode, name: curName });
            toast.success("Валюта добавлена");
            setCurCode(""); setCurName(""); setCurOpen(false);
            await load();
        } catch (err) {
            console.error("addCurrency:", err);
            toast.error("Не удалось добавить валюту");
        } finally {
            setSavingCur(false);
        }
    };

    const deleteCurrency = async (c) => {
        if (!window.confirm(`Удалить валюту «${c.name}»?`)) return;
        try {
            await axios.post(`${API_URL}/deleteCurrency`, { id: c.id });
            toast.success("Удалено");
            await load();
        } catch (err) {
            console.error("deleteCurrency:", err);
            toast.error("Не удалось удалить");
        }
    };

    const submitCategory = async () => {
        if (!catName.trim()) { toast.error("Укажите название"); return; }
        setSavingCat(true);
        try {
            await axios.post(`${API_URL}/addExpenseCategory`, {
                name: catName,
                parentId: catParentId || null,
                listSources: catParentId ? catListSources : [],
            });
            toast.success("Категория добавлена");
            setCatName(""); setCatParentId(""); setCatListSources([]);
            await load(); // форма остаётся открытой — ниже сразу видно новую категорию в превью
        } catch (err) {
            console.error("addExpenseCategory:", err);
            toast.error("Не удалось добавить категорию");
        } finally {
            setSavingCat(false);
        }
    };

    const deleteCategory = async (c) => {
        if (!window.confirm(`Удалить «${c.name}»?`)) return;
        try {
            await axios.post(`${API_URL}/deleteExpenseCategory`, { id: c.id });
            toast.success("Удалено");
            await load();
        } catch (err) {
            console.error("deleteExpenseCategory:", err);
            toast.error("Не удалось удалить");
        }
    };

    const toggleCatListSource = (key, checked) => {
        setCatListSources((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
    };

    const fetchHistory = (kind, id) => async () => {
        const { data } = await axios.post(`${API_URL}/getKassaHistory`, kind === "pt" ? { paymentTypeId: id } : { kassaId: id });
        return data.status === "success" ? data.history : [];
    };

    return (
        <div className="finance">
            <div className="fn-topbar">
                <div className="fn-pagetitle" onClick={handleTitleClick} style={{ userSelect: "none", cursor: "default" }}>
                    Касса — балансы
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {editMode && (
                        <>
                            <button className="kz-history-btn" onClick={() => setCurOpen((v) => !v)}>+ Валюта</button>
                            <button className="kz-history-btn" onClick={() => setPtOpen((v) => !v)}>+ Тип оплаты</button>
                            <button className="kz-history-btn" onClick={() => setAddCardOpen((v) => !v)}>+ Добавить карту</button>
                            <button className="kz-history-btn" onClick={() => setCatOpen((v) => !v)}>+ Категория</button>
                            <button className="kz-history-btn kz-adjust-btn" onClick={() => setAdjustOpen((v) => !v)}>⚖ Корректировка</button>
                        </>
                    )}
                </div>
            </div>

            <div className="fn-tabs">
                <div className="fn-tab" onClick={() => navigate("/expend")}>Расходы</div>
                <div className="fn-tab active">Касса</div>
            </div>

            {adjustOpen && (
                <AdjustBalanceForm API_URL={API_URL} paymentTypes={paymentTypes} kassaList={kassaList} onSaved={load} onClose={() => setAdjustOpen(false)} />
            )}

            {curOpen && (
                <div className="kz-addform">
                    <div className="fn-fgrid two">
                        <div className="fn-field"><label>Код</label><input placeholder="Например: THB" value={curCode} onChange={(e) => setCurCode(e.target.value)} /></div>
                        <div className="fn-field"><label>Название</label><input placeholder="Например: Бат" value={curName} onChange={(e) => setCurName(e.target.value)} /></div>
                    </div>
                    <div className="fn-hint">Действующие валюты:</div>
                    <div className="fn-paytype" style={{ flexWrap: "wrap" }}>
                        {currencies.map((c) => (
                            <span key={c.id} className="kz-cur-chip">{c.code} <button onClick={() => deleteCurrency(c)}>✕</button></span>
                        ))}
                    </div>
                    <button className="fn-btn-add-payment" onClick={submitCurrency} disabled={savingCur}>{savingCur ? "Сохраняем…" : "Добавить валюту"}</button>
                </div>
            )}

            {ptOpen && (
                <div className="kz-addform">
                    <div className="fn-fgrid two">
                        <div className="fn-field"><label>Название</label><input placeholder="Например: Наличные EURO" value={ptName} onChange={(e) => setPtName(e.target.value)} /></div>
                        {!ptIsCard && (
                            <div className="fn-field">
                                <label>Валюта</label>
                                <select value={ptCurrencyId || ""} onChange={(e) => setPtCurrencyId(Number(e.target.value))}>
                                    {currencies.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--fn-text-soft)", margin: "6px 0 14px", cursor: "pointer" }}>
                        <input type="checkbox" checked={ptIsCard} onChange={(e) => setPtIsCard(e.target.checked)} style={{ width: "auto" }} />
                        Несколько получателей
                    </label>
                    <button className="fn-btn-add-payment" onClick={submitPaymentType} disabled={savingPt}>{savingPt ? "Сохраняем…" : "Добавить"}</button>
                </div>
            )}

            {addCardOpen && (
                <div className="kz-addform">
                    <div className="fn-fgrid two">
                        <div className="fn-field">
                            <label>Тип оплаты (карта)</label>
                            <select value={cardTypeId || ""} onChange={(e) => setCardTypeId(e.target.value ? Number(e.target.value) : null)}>
                                <option value="">— выбрать —</option>
                                {cardTypes.map((pt) => <option key={pt.id} value={pt.id}>{pt.name}</option>)}
                            </select>
                        </div>
                        <div className="fn-field">
                            <label>Валюта</label>
                            <select value={cardCurrencyId || ""} onChange={(e) => setCardCurrencyId(Number(e.target.value))}>
                                {currencies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="fn-field">
                        <label>Получатель — чья карта</label>
                        <input placeholder="Например: Тазабай ага" value={cardRecipient} onChange={(e) => setCardRecipient(e.target.value)} />
                    </div>
                    {cardTypeId && (
                        <>
                            <div className="fn-hint">Уже существующие карты этого типа:</div>
                            <div className="fn-paytype" style={{ flexWrap: "wrap" }}>
                                {kassaList.filter((k) => k.paymentTypeId === cardTypeId).length === 0 ? (
                                    <span className="fn-hint" style={{ margin: 0 }}>Пока нет</span>
                                ) : (
                                    kassaList.filter((k) => k.paymentTypeId === cardTypeId).map((k) => (
                                        <span key={k.id} className="kz-cur-chip">{k.recipient}</span>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                    <button className="fn-btn-add-payment" onClick={submitCard} disabled={savingCard}>{savingCard ? "Сохраняем…" : "Добавить карту"}</button>
                </div>
            )}

            {catOpen && (
                <div className="kz-addform">
                    <div className="fn-hint" style={{ marginTop: 0 }}>Оставь «Без родителя», если это основная категория (например «Транспорт»). Выбери родителя, если это подкатегория (например «Заправка» внутри «Транспорт»).</div>
                    <div className="fn-fgrid two">
                        <div className="fn-field"><label>Название</label><input placeholder="Например: Заправка" value={catName} onChange={(e) => setCatName(e.target.value)} /></div>
                        <div className="fn-field">
                            <label>Родительская категория</label>
                            <select value={catParentId} onChange={(e) => { setCatParentId(e.target.value); if (!e.target.value) setCatListSources([]); }}>
                                <option value="">— без родителя (основная) —</option>
                                {mainCats.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        </div>
                    </div>

                    {catParentId && (
                        <div className="fn-field" style={{ marginBottom: 14 }}>
                            <label>
                                Списки сотрудников для этой категории
                                <span style={{ fontWeight: 400, color: "var(--fn-text-soft)" }}> — можно отметить несколько, они объединятся в один список в «Прочем расходе»</span>
                            </label>
                            <div className="kz-list-checks">
                                {Object.entries(LIST_SOURCE_LABELS).map(([key, lbl]) => (
                                    <label key={key} className="kz-list-check">
                                        <input
                                            type="checkbox"
                                            checked={catListSources.includes(key)}
                                            onChange={(e) => toggleCatListSource(key, e.target.checked)}
                                        />
                                        {lbl}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="fn-hint">Уже существующие категории:</div>
                    <div className="kz-cat-preview">
                        {mainCats.length === 0 ? (
                            <span className="fn-hint" style={{ margin: 0 }}>Пока пусто</span>
                        ) : (
                            mainCats.map((m) => (
                                <div key={m.id} className="kz-cat-preview-group">
                                    <span className="kz-cat-preview-main">{m.name}</span>
                                    {subsOf(m.id).length > 0 && (
                                        <span className="kz-cat-preview-subs">
                                            {subsOf(m.id).map((s) => (
                                                <span key={s.id} className="kz-cat-preview-sub">
                                                    {s.name}{s.listSource ? ` · ${listSourceLabels(s.listSource).join(" + ")}` : ""}
                                                </span>
                                            ))}
                                        </span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    <button className="fn-btn-add-payment" onClick={submitCategory} disabled={savingCat} style={{ marginTop: 14 }}>
                        {savingCat ? "Сохраняем…" : "Добавить категорию"}
                    </button>
                    <button className="btn-cancel-panel" style={{ marginTop: 8 }} onClick={() => setCatOpen(false)}>Закрыть</button>
                </div>
            )}

            {loading ? (
                <div className="fn-empty">Загрузка…</div>
            ) : (
                <>
                    <div className="fn-section-label">Кассы</div>
                    {regularTypes.length === 0 ? (
                        <div className="fn-empty">Пока пусто</div>
                    ) : (
                        <div className="kz-grid">
                            {regularTypes.map((pt) => (
                                <BalanceCard
                                    key={pt.id}
                                    label={pt.name}
                                    sub={pt.currencyCode}
                                    currencyCode={pt.currencyCode}
                                    balance={pt.balance}
                                    editMode={editMode}
                                    onDelete={() => deletePaymentType(pt)}
                                    onHistory={fetchHistory("pt", pt.id)}
                                    visibility={{
                                        showInIncome: pt.showInIncome,
                                        showInExpense: pt.showInExpense,
                                        onToggle: (field, value) => togglePtVisibility(pt, field, value),
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {cardTypes.map((pt) => {
                        const cards = kassaList.filter((k) => k.paymentTypeId === pt.id);
                        return (
                            <React.Fragment key={pt.id}>
                                <div className="kz-section-head-row">
                                    <div className="fn-section-label" style={{ margin: 0 }}>{pt.name}</div>
                                    {editMode && (
                                        <button className="kz-delete-type-btn" onClick={() => deletePaymentType(pt)}>Удалить тип ✕</button>
                                    )}
                                </div>
                                {cards.length === 0 ? (
                                    <div className="fn-empty">Карт пока нет</div>
                                ) : (
                                    <div className="kz-grid">
                                        {cards.map((k) => (
                                            <BalanceCard
                                                key={k.id}
                                                label={k.recipient}
                                                sub={k.currencyCode}
                                                currencyCode={k.currencyCode}
                                                balance={k.balance}
                                                editMode={editMode}
                                                onDelete={async () => { await axios.post(`${API_URL}/deleteKassa`, { id: k.id }); toast.success("Удалено"); await load(); }}
                                                onHistory={fetchHistory("k", k.id)}
                                                visibility={{
                                                    showInIncome: k.showInIncome,
                                                    showInExpense: k.showInExpense,
                                                    onToggle: (field, value) => toggleKassaVisibility(k, field, value),
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}

                    <div className="fn-section-label">Категории расходов</div>
                    {mainCats.length === 0 ? (
                        <div className="fn-empty">Категорий пока нет</div>
                    ) : (
                        mainCats.map((m) => (
                            <div className="kz-cat-group" key={m.id}>
                                <div className="kz-cat-main">
                                    <span>{m.name}</span>
                                    {editMode && <button className="kz-cat-del" onClick={() => deleteCategory(m)}>Удалить ✕</button>}
                                </div>
                                {subsOf(m.id).length > 0 && (
                                    <div className="kz-cat-subs">
                                        {subsOf(m.id).map((s) => (
                                            <span className="kz-cat-sub" key={s.id}>
                                                {s.name}
                                                {s.listSource && (
                                                    <span className="kz-cat-list-tag">{listSourceLabels(s.listSource).join(" + ")}</span>
                                                )}
                                                {editMode && <button onClick={() => deleteCategory(s)}>✕</button>}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </>
            )}
        </div>
    );
};

export default Kassa;